'use client';
import { useRouter } from 'next/navigation';

import { SRMatchStatus, SRStatus, SupplierRequest } from '@scaleup/models';
import { IconArrowRight } from '@tabler/icons-react';
import classNames from 'classnames';
import {
  useArchiveSupplierRequestMutationMutation,
  useUpdateImportedToActiveMutation,
} from '../../../api';
import { useAlert } from '../../../hooks';
import {
  AccordionV2,
  Button,
  ButtonWithMenu,
  Conditional,
  Tag,
  TagColor,
} from '../../../ui';
import {
  isNilString,
  snakeCaseToWords,
  unixToHumanReadable,
} from '../../../utils';

const SupplierRequestKeyRequirements = ({
  supplierRequest,
  expandable,
}: {
  supplierRequest: SupplierRequest;
  expandable: boolean;
}) => {
  const reqs = Object.keys(supplierRequest.requirements || {});

  if (reqs.length < 1) {
    return null;
  }

  return (
    <div className="p-5 rounded-[1rem] bg-[#f1f3ff]">
      <div>
        <p className="font-bold text-text-secondary">Generated Requirements:</p>
      </div>
      <div
        className={classNames('flex flex-wrap gap-1', { 'pb-6': expandable })}
      >
        {/* @ts-ignore */}
        {reqs.map((it, idx) => (
          <Tag key={idx} color="violet" text={it} />
        ))}
      </div>

      <Conditional condition={expandable}>
        <div>
          <AccordionV2
            hideLabel="Hide Details"
            showLabel="View Full Requirement Details"
            borderStyles="border-t-[#c7d0ff] border-t-[1px]"
            open={false}
          >
            <div className="grid grid-rows-1 gap-4">
              {reqs.map((req, idx) => (
                <div key={idx}>
                  <p className="font-bold text-text-secondary">{`${req}: `}</p>
                  <ul className="list-disc pl-4">
                    {((supplierRequest.requirements || {})[req] || []).map(
                      (item) => (
                        <li key={item}>{item}</li>
                      )
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </AccordionV2>
        </div>
      </Conditional>
    </div>
  );
};

const getServiceLocations = (locations?: string | string[]) => {
  if (!locations) {
    return [];
  }

  if (typeof locations === 'string') {
    if (locations.includes(';')) {
      return locations.split(';');
    }
    return locations.split(',');
  }

  return locations;
};

const SupplierRequestBasicDetails = ({
  supplierRequest,
}: {
  supplierRequest: SupplierRequest;
}) => (
  <div className="p-5 rounded-[1rem] bg-[#e9f2ee] flex gap-8">
    <div className="flex flex-col gap-2 border-r-[#c7e7d2] border-r-[1px] pr-8">
      {/* creation date */}
      <div className="flex gap-1">
        <span className="font-bold text-text-secondary">Creation Date:</span>
        <span className="text-text-secondary">
          {unixToHumanReadable(supplierRequest.created, 'MM/dd/yyyy')}
        </span>
      </div>
      {/* source */}
      <div className="flex gap-1">
        <span className="font-bold text-text-secondary">Source:</span>
        <span className="text-text-secondary">
          {(supplierRequest.source &&
            snakeCaseToWords(supplierRequest.source)) ||
            'Procurement System'}
        </span>
      </div>
      {/* owner */}
      <Conditional condition={!isNilString(supplierRequest.creatorName)}>
        <div className="flex gap-1">
          <span className="font-bold text-text-secondary">Owner:</span>
          <span className="text-text-secondary">
            {[supplierRequest.creatorName, supplierRequest.creatorTitle]
              .filter((it) => !isNilString(it))
              .join(', ')}
          </span>
        </div>
      </Conditional>
    </div>
    <div className="flex flex-col gap-2 pl-3">
      {/* categories */}
      <Conditional
        condition={
          !isNilString(supplierRequest.category) ||
          !isNilString(supplierRequest.subCategory)
        }
      >
        <div className="flex gap-1">
          <span className="font-bold text-text-secondary">Categories:</span>
          <span className="text-text-secondary">
            {[supplierRequest.category, supplierRequest.subCategory]
              .filter((it) => !isNilString(it))
              .join(', ')}
          </span>
        </div>
      </Conditional>
      {/* service locations */}
      <Conditional condition={getServiceLocations().length > 0}>
        <div className="flex gap-1">
          <span className="font-bold text-text-secondary">
            Service Locations:
          </span>
          <span className="text-text-secondary">
            {getServiceLocations().join(', ')}
          </span>
        </div>
      </Conditional>
      {/* preferred minority group */}
      <Conditional
        condition={
          supplierRequest.supplierPreference &&
          supplierRequest.supplierPreference.length > 0
        }
      >
        <div className="flex gap-1">
          <span className="font-bold text-text-secondary">
            Preferred Minority Group:
          </span>
          <span className="text-text-secondary">
            {(supplierRequest.supplierPreference || []).join(', ')}
          </span>
        </div>
      </Conditional>
    </div>
  </div>
);

const SupplierRequestSummary = ({
  supplierRequest,
}: {
  supplierRequest: SupplierRequest;
}) => (
  <div className="p-5 rounded-[1rem] bg-[#fff3f1] flex flex-col gap-4">
    <p className="font-bold text-text-secondary">Provided Information:</p>
    <p className="text-text-secondary">{supplierRequest.description}</p>
  </div>
);

export const SupplierRequestCardBase = ({
  supplierRequest,
  cta,
  expandableRequirements = false,
  showSummary = false,
  title,
}: {
  supplierRequest: SupplierRequest;
  title: string | JSX.Element;
  cta: JSX.Element | null;
  expandableRequirements?: boolean;
  showSummary?: boolean;
}) => {
  return (
    <div className="flex gap-8 flex-col">
      <div className="flex gap-2 flex-col">
        <div className="w-full flex justify-between	items-center gap-2">
          <h2 className="text-xl w-full font-bold">{title}</h2>
          {cta}
        </div>
      </div>
      <SupplierRequestBasicDetails supplierRequest={supplierRequest} />

      <Conditional condition={showSummary}>
        <SupplierRequestSummary supplierRequest={supplierRequest} />
      </Conditional>

      <SupplierRequestKeyRequirements
        expandable={expandableRequirements}
        supplierRequest={supplierRequest}
      />
    </div>
  );
};

export const SupplierRequestListCard = ({
  supplierRequest,
}: {
  supplierRequest: SupplierRequest;
}) => {
  const router = useRouter();
  const setAlert = useAlert();
  const [archive] = useArchiveSupplierRequestMutationMutation();
  const [updateToActive, { isLoading: updateLoading }] =
    useUpdateImportedToActiveMutation();

  const moveToArchive = async () => {
    await archive(supplierRequest.requestId);
    setAlert({
      severity: 'success',
      title: 'Request Archived',
      show: true,
    });
  };

  const viewDetails = () =>
    router.push(`/supplier-requests/${supplierRequest.requestId}`);

  const moveToActive = async () => {
    await updateToActive(supplierRequest.requestId);
    setAlert({
      severity: 'success',
      title: 'Update Successful',
      message: `${supplierRequest.projectName} moved to Active status`,
      show: true,
    });
  };

  const supplierRequestCTA = (supplierRequest: SupplierRequest) => {
    if (supplierRequest.status === SRStatus.ARCHIVED) {
      return (
        <div className="w-[200px]">
          <Button
            text="View Details"
            variant="primary"
            suffixIcon={<IconArrowRight />}
            onClick={viewDetails}
          />
        </div>
      );
    }

    if (supplierRequest.status === SRStatus.IMPORTED) {
      return (
        <div className="w-[200px]">
          <Button
            text="Move to Active"
            variant="primary"
            loading={updateLoading}
            onClick={moveToActive}
          />
        </div>
      );
    }

    return (
      <ButtonWithMenu
        variant="primary"
        items={[
          {
            name: 'Archive',
            onClick: () => moveToArchive,
          },
        ]}
        onClick={viewDetails}
      >
        View Details
      </ButtonWithMenu>
    );
  };

  return (
    <SupplierRequestCardBase
      title={
        <div className="flex gap-2 items-center">
          <div>{supplierRequest.projectName}</div>{' '}
          <SupplierRequestStatus status={supplierRequest.matchStatus} />
        </div>
      }
      cta={supplierRequestCTA(supplierRequest)}
      supplierRequest={supplierRequest}
    />
  );
};

const STATUS_TO_COLOR: Record<SRMatchStatus, TagColor> = {
  NOT_STARTED: 'gray',
  IN_PROGRESS: 'blue',
  MATCHES_FOUND: 'blue',
  NO_MATCHES_FOUND: 'orange',
  MATCHING_COMPLETED: 'green',
};

export const SupplierRequestStatus = ({
  status,
}: {
  status: SRMatchStatus;
}) => {
  return (
    <Tag color={STATUS_TO_COLOR[status]} text={`${snakeCaseToWords(status)}`} />
  );
};
